import { useEffect, useRef, useState, type ReactNode } from "react";
import { IonContent, IonPage, useIonRouter } from "@ionic/react";
import { useLocation, useParams } from "react-router-dom";

import { ActionBar } from "../components/ActionBar";
import { Hero } from "../components/Hero";
import { I } from "../components/icons";
import { Notice } from "../components/Notice";
import { Ring } from "../components/Ring";
import { NavRow, RowGroup } from "../components/Rows";
import { Screen } from "../components/Shell";
import { Sheet } from "../components/Sheet";
import { Tag } from "../components/Tag";
import { TopBar } from "../components/TopBar";
import { age } from "../domain/body";
import { addDays, dayLabel, todayStr } from "../domain/date";
import { older } from "../domain/eating";
import {
  BALANCE_STAGES,
  TANDEM_SECS,
  TESTS,
  levelSentence,
  ownerPhase,
  ownerPhaseMode,
  placement,
  placementReason,
  reading,
  RETEST_DAYS,
  testsFor,
  type TestId,
  shortTestsTitle,
} from "../domain/fitnessTests";
import { phase } from "../domain/plan";
import type { DayTests } from "../domain/types";
import { beep, buzz, setWakeLock } from "../lib/device";
import { useTimer } from "../lib/useTimer";
import { useDeviceAnswers, useKaya } from "../store/kayaStore";
import { setTests, setTrainingProfile } from "../store/toggles";
import "../components/PlayerShell.css";
import "./TrainTests.css";

type Results = Partial<Omit<DayTests, "at">>;

type Stage = { at: "intro" } | { at: "run"; test: TestId; queue: TestId[] } | { at: "result" };

const ICON: Record<TestId, ReactNode> = {
  chair: I.legs,
  balance: I.balance,
  tug: I.walk,
  step: I.footsteps,
  pushups: I.upper,
  plank: I.core,
};

const LEFT_TITLE: Record<TestId, string> = {
  chair: "No chair stand",
  balance: "No balance test",
  tug: "No up-and-go",
  step: "No step test",
  pushups: "No push-ups",
  plank: "No plank",
};

/** "0:18" */
function clock(secs: number): string {
  const s = Math.max(0, Math.ceil(secs));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** a path inside the app, so `?then=` cannot send the person anywhere else */
function safeThen(v: string | null): string | null {
  return v && v.startsWith("/") && !v.startsWith("//") ? v : null;
}

/**
 * /train/tests and /train/tests/:test (boards TrainTestIntro, TrainTestChairStand,
 * TrainTestBalance, TrainTestResult; ADR-054). The intro names the tests this person's answers
 * allow and why one is left out, each test runs on the player's surface with the phone timing and
 * the person beside counting, and the result shows every figure beside its published range,
 * the placement in one sentence and the retest date. Nothing is written until Save; `?then=`
 * (Plan my exercise's "Do them now") is where Save and Close go back to.
 *
 * One `IonPage` whatever the stage, as the players do: a second root would remount mid-route.
 */
export default function TrainTests() {
  const params = useParams<{ test?: string }>();
  const location = useLocation();
  const router = useIonRouter();
  const S = useKaya((s) => s.S);
  const update = useKaya((s) => s.update);
  const showToast = useKaya((s) => s.showToast);
  const setTrainingDraft = useKaya((s) => s.setTrainingDraft);
  const device = useDeviceAnswers();
  const then = safeThen(new URLSearchParams(location.search).get("then"));
  const exitTo = then ?? "/train";

  const now = new Date();
  const today = todayStr(now);
  const old = older(S, now);
  const [alone, setAlone] = useState(false);
  const plan = testsFor(S, S.profile.training ?? {}, device, { alone }, now);
  const offered = [...plan.required, ...plan.optional];

  const [res, setRes] = useState<Results>({});
  const [done, setDone] = useState<TestId[]>([]);
  const [stage, setStage] = useState<Stage>(() => {
    const t = params.test as TestId | undefined;
    return t && t in TESTS && offered.includes(t) ? { at: "run", test: t, queue: [] } : { at: "intro" };
  });

  const run = (queue: TestId[]) => {
    if (!queue.length) setStage({ at: "result" });
    else setStage({ at: "run", test: queue[0], queue: queue.slice(1) });
  };
  const startAll = () => run(plan.required.filter((t) => !done.includes(t)));
  const finish = (test: TestId, patch: Results) => {
    setRes((r) => ({ ...r, ...patch }));
    setDone((d) => (d.includes(test) ? d : [...d, test]));
    if (stage.at === "run") run(stage.queue);
  };

  useEffect(() => {
    void setWakeLock(stage.at === "run");
    return () => void setWakeLock(false);
  }, [stage.at]);

  const save = () => {
    const placed = placement(S, res, now);
    const mode = ownerPhaseMode(S, res, phase(S), now);
    const before = S.profile.training?.at;
    update((d) => {
      setTests(d, today, res);
      if (placed) setTrainingProfile(d, { level: placed.level });
      if (mode !== null) d.profile.phaseMode = mode;
    });
    // The level moves the block's stamp, and Plan my exercise drops a draft whose stamp moved
    // ("saved since on another phone"): taken from the questionnaire's tests step, every answer
    // so far was lost. This write is the draft's own, so the draft follows the stamp. (P9)
    const draft = useKaya.getState().meta.trainingDraft;
    const after = useKaya.getState().S.profile.training?.at;
    if (draft && draft.since === before && after !== before) setTrainingDraft({ ...draft, since: after });
    showToast("Tests saved");
    router.push(exitTo, "back");
  };

  if (stage.at === "run") {
    const batch = plan.required;
    const idx = batch.indexOf(stage.test);
    const title = `${TESTS[stage.test].title} · ${idx >= 0 ? `test ${idx + 1} of ${batch.length}` : "optional"}`;
    const back = () => setStage({ at: done.length ? "result" : "intro" });
    return (
      <IonPage className="k-player-page k-player--work">
        <IonContent>
          <div className="screen full">
            <div className="inner">
              {TESTS[stage.test].kind === "stages" ? (
                <BalanceTest key={stage.test} title={title} onStop={back} onDone={(p) => finish(stage.test, p)} />
              ) : TESTS[stage.test].kind === "stopwatch" ? (
                <StopwatchTest key={stage.test} test={stage.test} title={title} onStop={back} onDone={(p) => finish(stage.test, p)} />
              ) : TESTS[stage.test].kind === "count" ? (
                <CountTest key={stage.test} test={stage.test} title={title} onStop={back} onDone={(p) => finish(stage.test, p)} />
              ) : (
                <CountdownTest key={stage.test} test={stage.test} title={title} onStop={back} onDone={(p) => finish(stage.test, p)} />
              )}
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (stage.at === "result") {
    const years = age(S, now);
    const placed = placement(S, res, now);
    const owner = !old ? ownerPhase(res) : null;
    const shown = [...offered, ...done.filter((t) => !offered.includes(t))];
    return (
      <IonPage>
        <IonContent>
          <Screen>
            <TopBar title="Your tests" back={{ to: exitTo, close: true, label: "Close; the readings are not saved" }} />
            <div className="tt-page">
              {placed ? (
                <Hero
                  label={`Your tests · ${dayLabel(today)}`}
                  title={placed.level === "steady" ? "Steady" : "Build"}
                  line={levelSentence(placed.level)}
                  sub={placementReason(S, placed, res, now)}
                />
              ) : owner !== null ? (
                <Hero
                  label={`Your tests · ${dayLabel(today)}`}
                  title={`Phase ${owner + 1}`}
                  line={`Your wall push-ups${res.plank !== undefined ? " and plank" : ""} place you at phase ${owner + 1} of the Kaya programme.`}
                  sub={
                    S.profile.phaseMode === "auto"
                      ? "Kaya’s own rule: no published range exists for wall push-ups or a plank."
                      : "Your phase is pinned in You › Sessions, so Kaya leaves it as it is."
                  }
                />
              ) : (
                <Hero label={`Your tests · ${dayLabel(today)}`} title="Your figures" line="Each one beside its published range, where there is one." />
              )}

              <RowGroup heading="Your readings">
                {shown.map((t) => {
                  const r = reading(t, res, years, S.profile.sex);
                  return <NavRow key={t} kind="value" icon={ICON[t]} title={r.title} subtitle={r.detail} />;
                })}
              </RowGroup>
              <p className="tt-fine">
                {old
                  ? "Your figure beside a published range — US norms (Rikli & Jones; CDC STEADI); none exist for India. A timed task, not a diagnosis or a grade."
                  : "Your figures, never a grade. The chair stand and step test ranges start at 60; the push-ups and the plank have none."}
              </p>

              <RowGroup>
                <NavRow
                  kind="value"
                  icon={I.calendar}
                  title="Retest"
                  subtitle="four weeks on · Kaya reminds you on Train"
                  value={dayLabel(addDays(today, RETEST_DAYS))}
                />
              </RowGroup>
            </div>
            <ActionBar
              secondary={{ label: "Do one again", onClick: () => setStage({ at: "intro" }) }}
              primary={{ label: "Save", onClick: save, disabled: !done.length, reason: "No test done yet" }}
            />
          </Screen>
        </IonContent>
      </IonPage>
    );
  }

  /* ---------- the intro ---------- */
  const required = plan.required.length;
  const minutes = old ? 2 : 5;
  const left = plan.required.filter((t) => !done.includes(t));
  return (
    <IonPage>
      <IonContent>
        <Screen>
          <TopBar title="Your tests" back={{ to: exitTo, label: then ? "Back" : "Back to Train" }} />
          <div className="tt-page">
            {plan.held ? (
              <>
                <Hero label={`Your tests · ${dayLabel(today)}`} title="Not for now" line={plan.held} />
                <Notice tone="caution">Tell Kaya when your doctor says yes, in You › Training.</Notice>
              </>
            ) : (
              <>
                <Hero
                  label={`${shortTestsTitle(required)} · ${dayLabel(today)}`}
                  figure={minutes}
                  unit="minutes"
                  line={old ? "A chair, and someone beside you." : "A chair, a wall and a timer."}
                />
                <RowGroup heading="Today’s tests">
                  {offered.map((t) => {
                    const r = reading(t, res, age(S, now), S.profile.sex);
                    return (
                      <NavRow
                        key={t}
                        icon={ICON[t]}
                        title={
                          <>
                            {TESTS[t].title}
                            {plan.optional.includes(t) ? (
                              <>
                                {" "}
                                <Tag tone="neutral">optional</Tag>
                              </>
                            ) : null}
                          </>
                        }
                        subtitle={done.includes(t) ? r.title : TESTS[t].sub}
                        value={done.includes(t) ? "done" : undefined}
                        onClick={() => run([t])}
                      />
                    );
                  })}
                  {plan.left.map((l) => (
                    <NavRow
                      key={l.test}
                      kind="value"
                      icon={ICON[l.test]}
                      title={LEFT_TITLE[l.test]}
                      subtitle={l.why}
                    />
                  ))}
                </RowGroup>
                {old ? (
                  <RowGroup>
                    <NavRow
                      kind="toggle"
                      title="Someone is beside me"
                      subtitle="The balance test and the up-and-go need them"
                      checked={!alone}
                      onChange={(v) => setAlone(!v)}
                    />
                  </RowGroup>
                ) : null}
                <Notice tone="caution">
                  Stop if anything hurts, or you feel dizzy or short of breath.
                  {old ? " Keep the counter within reach for the balance test." : ""}
                </Notice>
                <p className="tt-fine">
                  {old
                    ? "A timed task with a published range, not a diagnosis. Kaya shows your figure beside the usual range for your age."
                    : "They place you in the programme instead of starting at week 1. Your figures, never a grade."}
                </p>
              </>
            )}
          </div>
          {plan.held ? (
            <ActionBar primary={{ label: "Back", onClick: () => router.push(exitTo, "back") }} />
          ) : (
            <ActionBar
              primary={
                left.length
                  ? { label: done.length ? "Carry on" : "Start", icon: I.play, onClick: startAll }
                  : { label: "See the result", onClick: () => setStage({ at: "result" }), disabled: !done.length, reason: "No test done yet" }
              }
            />
          )}
        </Screen>
      </IonContent>
    </IonPage>
  );
}

/* ---------- the test screens (the player's surface; boards TrainTestChairStand, TrainTestBalance) ---------- */

interface TestProps {
  title: string;
  onStop: () => void;
  onDone: (patch: Results) => void;
}

function Top({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div className="k-player__top">
      <button type="button" className="k-iconbtn k-iconbtn--on-surface" aria-label="Stop the test" onClick={onClose}>
        {I.close}
      </button>
      <p className="k-player__title">{title}</p>
    </div>
  );
}

function Dial({ pct, figure, unit, label }: { pct: number; figure: string; unit: string; label: string }) {
  return (
    <div className="k-player__figure">
      <div className="k-player__ring" role="img" aria-label={label}>
        <Ring
          pct={pct}
          size={248}
          stroke={12}
          color="var(--on-surface)"
          track="var(--on-surface-track)"
          style={{ width: "var(--k-player-ring)", height: "var(--k-player-ring)" }}
        >
          <div aria-hidden="true" className="k-player__ringc">
            <div className="k-player__fig">{figure}</div>
            <div className="k-player__unit">{unit}</div>
          </div>
        </Ring>
      </div>
    </div>
  );
}

function useSound() {
  const sound = useKaya((s) => s.S.profile.sound);
  const vibrate = useKaya((s) => s.S.profile.vibrate);
  return {
    tick: () => beep(880, 80, 0.1, sound),
    go: () => {
      beep(1046, 160, 0.16, sound);
      buzz(80, vibrate);
    },
    end: () => {
      beep(1318, 300, 0.2, sound);
      buzz([120, 80, 120], vibrate);
    },
  };
}

/** 30-second chair stand and 2-minute step: a countdown, and one big tap for the person beside. */
function CountdownTest({ test, title, onStop, onDone }: TestProps & { test: TestId }) {
  const p = TESTS[test];
  const secs = p.secs ?? 30;
  const snd = useSound();
  const [count, setCount] = useState(0);
  const [state, setState] = useState<"ready" | "running" | "over">("ready");
  const [asking, setAsking] = useState(false);
  const timer = useTimer(
    (s) => {
      if (s <= 3 && s > 0) snd.tick();
    },
    () => {
      snd.end();
      setState("over");
    }
  );
  const left = state === "ready" ? secs : state === "over" ? 0 : timer.remaining / 1000;
  const tap = () => {
    if (state === "ready") {
      setState("running");
      snd.go();
      timer.start(secs);
      return;
    }
    setCount((c) => c + 1);
  };
  const result = (): Results => (test === "chair" ? { chairStand: count, chairArms: undefined } : { step: count });

  return (
    <div className="k-player">
      <Top title={title} onClose={() => (state === "ready" ? onStop() : setAsking(true))} />
      <p className="tt-rule">{p.rule}</p>
      <Dial
        pct={left / secs}
        figure={clock(left)}
        unit={state === "ready" ? `${clock(secs)} when you start` : state === "over" ? "time" : `left of ${clock(secs)}`}
        label={state === "over" ? "Time is up" : `${clock(left)} left of ${clock(secs)}`}
      />
      <div className="tt-tapwrap">
        <button
          type="button"
          className="tt-tap"
          onClick={tap}
          aria-label={state === "ready" ? "Start the clock" : `Count one. ${count} so far`}
        >
          <span className="tt-tap__n">{state === "ready" ? "Start" : count}</span>
          <span className="tt-tap__w">{state === "ready" ? `Then ${p.tap?.toLowerCase()}` : p.tap}</span>
        </button>
        <p className="tt-help">
          {test === "chair"
            ? "For the person beside you: one tap each time they are fully up. A stand more than halfway up at 0:00 counts."
            : "For the person beside you: one tap each time the right knee reaches the mark."}
        </p>
      </div>
      <div className="tt-gap" />
      <div className="tt-ctl">
        {state === "over" ? (
          <>
            <button
              type="button"
              className="tt-btn"
              onClick={() => {
                setCount(0);
                setState("ready");
              }}
            >
              Again
            </button>
            <button type="button" className="tt-btn tt-btn--primary" onClick={() => onDone(result())}>
              Next
            </button>
          </>
        ) : (
          <>
            <button type="button" className="tt-btn" onClick={() => (state === "ready" ? onStop() : setAsking(true))}>
              Stop
            </button>
            <button type="button" className="tt-btn" disabled={count === 0} onClick={() => setCount((c) => Math.max(0, c - 1))}>
              {I.undo}
              Undo a tap
            </button>
          </>
        )}
      </div>
      {asking ? (
        <Sheet
          title={`Stop the ${p.title.toLowerCase()}?`}
          sub={test === "chair" ? "If the arms were needed to stand, STEADI scores the test 0." : "The count so far is not kept."}
          onClose={() => setAsking(false)}
          footer={
            test === "chair" ? (
              <ActionBar
                inSheet
                secondary={{ label: "Keep going", onClick: () => setAsking(false) }}
                also={{
                  label: "No score",
                  onClick: () => {
                    timer.stop();
                    onStop();
                  },
                }}
                primary={{
                  label: "Arms needed: 0",
                  onClick: () => {
                    timer.stop();
                    onDone({ chairStand: 0, chairArms: true });
                  },
                }}
              />
            ) : (
              <ActionBar
                inSheet
                secondary={{ label: "Keep going", onClick: () => setAsking(false) }}
                primary={{
                  label: "Stop, no score",
                  onClick: () => {
                    timer.stop();
                    onStop();
                  },
                }}
              />
            )
          }
        >
          <p className="tt-sheetline">
            {test === "chair"
              ? "Arms needed records 0 stands with “used the arms”, as the test’s rules say. No score keeps nothing."
              : "Stop now and nothing is kept; you can do it at the retest."}
          </p>
        </Sheet>
      ) : null}
    </div>
  );
}

/** The 4-stage balance test: each stage held 10 s, stopping at the first that is not. */
function BalanceTest({ title, onStop, onDone }: TestProps) {
  const snd = useSound();
  const [i, setI] = useState(0);
  const [running, setRunning] = useState(false);
  const iRef = useRef(0);
  iRef.current = i;
  const held = () => {
    snd.end();
    setRunning(false);
    const at = iRef.current;
    if (at >= 3) onDone({ balanceStage: 4, balanceSecs: undefined });
    else setI(at + 1);
  };
  const timer = useTimer(undefined, held);
  // seconds held, counted up and shown whole: 6.8 s held is "0:06", as the person beside saw it
  const secs = running ? Math.floor(TANDEM_SECS - timer.remaining / 1000) : 0;
  const stage = BALANCE_STAGES[i];

  return (
    <div className="k-player">
      <Top title={title} onClose={onStop} />
      <ol className="tt-stages" aria-label="The four stages">
        {BALANCE_STAGES.map((s, k) => (
          <li key={s.name} className={`tt-stage${k === i ? " is-now" : k > i ? " is-later" : ""}`} aria-current={k === i ? "step" : undefined}>
            <span className="tt-stage__mark" aria-hidden="true">
              {k < i ? I.tick : k + 1}
            </span>
            <span className="tt-stage__txt">
              <span className="tt-stage__name">
                {k + 1} · {s.name}
              </span>
              <span className="tt-stage__sub">{k < i ? s.sub : k === i ? (running ? "holding" : "ready") : "not reached yet"}</span>
            </span>
            <span className="tt-stage__val">{k < i ? "10 s" : k === i ? clock(secs) : "–"}</span>
          </li>
        ))}
      </ol>
      <Dial pct={secs / TANDEM_SECS} figure={clock(secs)} unit={`of ${clock(TANDEM_SECS)}`} label={`${stage.name}: ${secs} of 10 seconds`} />
      <div className="k-player__meta">
        <p className="k-player__name">{stage.name}</p>
        <p className="k-player__cue">{stage.cue}</p>
      </div>
      <div className="tt-gap" />
      <div className="tt-ctl">
        <button
          type="button"
          className="tt-btn"
          onClick={() => {
            timer.stop();
            onDone({ balanceStage: i as 0 | 1 | 2 | 3, balanceSecs: secs });
          }}
        >
          Couldn’t hold
        </button>
        <button
          type="button"
          className="tt-btn tt-btn--primary"
          onClick={() => {
            if (!running) {
              setRunning(true);
              snd.go();
              timer.start(TANDEM_SECS);
            } else {
              // a clock started late: the person beside saw the 10 seconds
              timer.stop();
              held();
            }
          }}
        >
          {running ? "Next stage" : "Start"}
        </button>
      </div>
    </div>
  );
}

/** The up-and-go and the plank: a stopwatch the person beside starts and stops. */
function StopwatchTest({ test, title, onStop, onDone }: TestProps & { test: TestId }) {
  const p = TESTS[test];
  const snd = useSound();
  const [startAt, setStartAt] = useState<number | null>(null);
  const [stopAt, setStopAt] = useState<number | null>(null);
  const [, tick] = useState(0);
  useEffect(() => {
    if (startAt === null || stopAt !== null) return;
    const iv = setInterval(() => tick((n) => n + 1), 100);
    return () => clearInterval(iv);
  }, [startAt, stopAt]);
  const secs = startAt === null ? 0 : ((stopAt ?? Date.now()) - startAt) / 1000;
  const value = test === "tug" ? Math.round(secs * 10) / 10 : Math.floor(secs);
  const span = test === "tug" ? 30 : 120;
  const figure = test === "tug" ? value.toFixed(1) : clock(value);

  return (
    <div className="k-player">
      <Top title={title} onClose={onStop} />
      <p className="tt-rule">{p.rule}</p>
      <Dial
        pct={Math.min(1, secs / span)}
        figure={figure}
        unit={test === "tug" ? "seconds" : "held"}
        label={`${figure} ${test === "tug" ? "seconds" : "held"}`}
      />
      <div className="k-player__meta">
        <p className="k-player__cue">
          {test === "tug"
            ? "Start the clock on “go”; stop it when they are sitting again."
            : "Start the clock once the plank is set; stop it when the back sags or the breath is held."}
        </p>
      </div>
      <div className="tt-gap" />
      <div className="tt-ctl">
        {stopAt !== null ? (
          <>
            <button
              type="button"
              className="tt-btn"
              onClick={() => {
                setStartAt(null);
                setStopAt(null);
              }}
            >
              Again
            </button>
            <button type="button" className="tt-btn tt-btn--primary" onClick={() => onDone(test === "tug" ? { tug: value } : { plank: value })}>
              Next
            </button>
          </>
        ) : (
          <>
            <button type="button" className="tt-btn" onClick={onStop}>
              Stop
            </button>
            <button
              type="button"
              className="tt-btn tt-btn--primary"
              onClick={() => {
                if (startAt === null) {
                  snd.go();
                  setStartAt(Date.now());
                } else {
                  snd.end();
                  setStopAt(Date.now());
                }
              }}
            >
              {startAt === null ? "Start the clock" : "Stop the clock"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/** Wall push-ups: no clock, a count. */
function CountTest({ test, title, onStop, onDone }: TestProps & { test: TestId }) {
  const p = TESTS[test];
  const [count, setCount] = useState(0);
  return (
    <div className="k-player">
      <Top title={title} onClose={onStop} />
      <p className="tt-rule">{p.rule}</p>
      <div className="tt-gap" />
      <div className="tt-tapwrap">
        <button type="button" className="tt-tap tt-tap--tall" onClick={() => setCount((c) => c + 1)} aria-label={`Count one. ${count} so far`}>
          <span className="tt-tap__n">{count}</span>
          <span className="tt-tap__w">{p.tap}</span>
        </button>
      </div>
      <div className="tt-gap" />
      <div className="tt-ctl">
        <button type="button" className="tt-btn" disabled={count === 0} onClick={() => setCount((c) => Math.max(0, c - 1))}>
          {I.undo}
          Undo a tap
        </button>
        <button type="button" className="tt-btn tt-btn--primary" onClick={() => onDone({ pushups: count })}>
          Done
        </button>
      </div>
    </div>
  );
}
